// courseData.tsx

// --- Types ---
export type Difficulty = "Beginner" | "Intermediate" | "Advanced";

export type LessonIcon =
  | "CodeOutlined"
  | "DatabaseOutlined"
  | "ControlOutlined"
  | "FunctionOutlined"
  | "ApiOutlined"
  | "EditOutlined"
  | "RocketOutlined";

export interface Lesson {
  id: string;
  title: string;
  iconName?: LessonIcon;
  type: "lesson" | "exercise";
  difficulty: Difficulty;
  content: string;
  starterCode?: string;
  solution?: string;
}

export interface Module {
  id: string;
  title: string;
  lessons: Lesson[];
}

// --- Comprehensive Course Curriculum ---
const courseCurriculum: Module[] = [
  {
    id: "m1",
    title: "Python Foundations",
    lessons: [
      {
        id: "l1",
        title: "Welcome to Python!",
        iconName: "CodeOutlined",
        type: "lesson",
        difficulty: "Beginner",
        content:
          'Welcome to Python!\n\nPython is a versatile, high-level programming language widely used in data science, artificial intelligence, and web development. Its design philosophy emphasizes code readability, making it an excellent first language to learn.\n\nIn Python, you do not need to declare variable types explicitly (like in Java or C++). You simply assign a value to a name using the `=` operator.\n\nExample:\n`message = "Hello, World!"`\n`print(message)`',
      },
      {
        id: "l2",
        title: "Variables and Types",
        iconName: "DatabaseOutlined",
        type: "exercise",
        difficulty: "Beginner",
        content:
          "Let's practice creating variables for a spaceship's dashboard.\n\nPython has several built-in data types:\n* **Integers** (whole numbers)\n* **Floats** (decimals)\n* **Strings** (text enclosed in quotes)\n\nCreate a variable called `ship_name` and assign it the string `\"Apollo\"`.\nCreate a variable called `fuel_level` and assign it the float `85.5`.\nCreate a variable called `crew_size` and assign it the integer `3`.\n\nFinally, print all three variables.",
        starterCode:
          "# 1. Create your variables here\n\n\n# 2. Print your variables\n",
        solution:
          'ship_name = "Apollo"\nfuel_level = 85.5\ncrew_size = 3\nprint(ship_name)\nprint(fuel_level)\nprint(crew_size)',
      },
      {
        id: "l3",
        title: "Basic Arithmetic",
        iconName: "EditOutlined",
        type: "exercise",
        difficulty: "Beginner",
        content:
          "Python makes for a powerful calculator. It supports standard operators like `+`, `-`, `*`, and `/`.\n\nLet's calculate the daily revenue of a local coffee shop.\n\n1. Create `coffees_sold = 145`\n2. Create `price_per_coffee = 4.50`\n3. Multiply them and store in `total_revenue`\n4. Print `total_revenue`.",
        starterCode:
          "coffees_sold = 145\nprice_per_coffee = 4.50\n\n# Calculate revenue\n\n\n# Print revenue\n",
        solution:
          "coffees_sold = 145\nprice_per_coffee = 4.50\ntotal_revenue = coffees_sold * price_per_coffee\nprint(total_revenue)",
      },
      {
        id: "l4",
        title: "String Magic (f-strings)",
        iconName: "CodeOutlined",
        type: "exercise",
        difficulty: "Beginner",
        content:
          'Use f-strings to combine variables and text.\n\nExample:\n`print(f"Hello {name}!")`\n\nUsing the variables below, print exactly:\n`"Welcome to Python, Alex! You have 5 unread messages."`',
        starterCode:
          'user = "Alex"\nmessages = 5\n\n# Print the formatted string below\n',
        solution:
          'user = "Alex"\nmessages = 5\nprint(f"Welcome to Python, {user}! You have {messages} unread messages.")',
      },
    ],
  },
  {
    id: "m2",
    title: "Control Flow & Logic",
    lessons: [
      {
        id: "l5",
        title: "Booleans & Comparisons",
        iconName: "ControlOutlined",
        type: "lesson",
        difficulty: "Beginner",
        content:
          "Booleans can only be `True` or `False`.\n\nComparison operators:\n* `==`\n* `!=`\n* `>`\n* `<`\n* `>=`\n* `<=`\n\nExample:\n`print(10 > 5)` → True",
      },
      {
        id: "l6",
        title: "If / Elif / Else",
        iconName: "ControlOutlined",
        type: "exercise",
        difficulty: "Beginner",
        content:
          'Write an `if/else` statement:\n\n1. Check if `rider_height >= 120`\n2. If true, print `"Enjoy the ride!"`\n3. Otherwise print `"Sorry, too short."`',
        starterCode:
          "rider_height = 115\n\n# Write your if/else statement below:\n",
        solution:
          'rider_height = 115\nif rider_height >= 120:\n    print("Enjoy the ride!")\nelse:\n    print("Sorry, too short.")',
      },
      {
        id: "l7",
        title: "The While Loop",
        iconName: "RocketOutlined",
        type: "exercise",
        difficulty: "Intermediate",
        content:
          'Create a countdown using `while countdown > 0`.\nPrint each number and decrement by 1.\nFinally print `"Liftoff!"`',
        starterCode: "countdown = 3\n\n# Write your while loop here:\n\n\n",
        solution:
          'countdown = 3\nwhile countdown > 0:\n    print(countdown)\n    countdown = countdown - 1\nprint("Liftoff!")',
      },
      {
        id: "l8",
        title: "The For Loop",
        iconName: "RocketOutlined",
        type: "exercise",
        difficulty: "Intermediate",
        content:
          "Loop through `scores = [7, 9, 8, 10]` and print each score multiplied by 10.",
        starterCode: "scores = [7, 9, 8, 10]\n\n# Write your for loop here:\n",
        solution:
          "scores = [7, 9, 8, 10]\nfor score in scores:\n    print(score * 10)",
      },
    ],
  },
  {
    id: "m3",
    title: "Data Structures",
    lessons: [
      {
        id: "l9",
        title: "Lists and Indexing",
        iconName: "DatabaseOutlined",
        type: "lesson",
        difficulty: "Intermediate",
        content:
          'Lists are ordered collections.\n\nExample:\n`colors = ["red", "green", "blue"]`\n`print(colors[0])`',
      },
      {
        id: "l10",
        title: "List Methods",
        iconName: "DatabaseOutlined",
        type: "exercise",
        difficulty: "Intermediate",
        content:
          '1. Add `"milk"` to `cart`\n2. Change `"apple"` to `"green apple"`\n3. Print cart',
        starterCode:
          'cart = ["apple", "bread", "eggs"]\n\n# Modify the list here\n',
        solution:
          'cart = ["apple", "bread", "eggs"]\ncart.append("milk")\ncart[0] = "green apple"\nprint(cart)',
      },
      {
        id: "l11",
        title: "Dictionaries",
        iconName: "DatabaseOutlined",
        type: "exercise",
        difficulty: "Intermediate",
        content:
          'Create a dictionary:\n`{"health": 100, "weapon": "Sword"}`\nThen add `"gold": 50`.',
        starterCode:
          "# Create and modify your dictionary below\n\n\n# Print the dictionary\n",
        solution:
          'player = {"health": 100, "weapon": "Sword"}\nplayer["gold"] = 50\nprint(player)',
      },
      {
        id: "l12",
        title: "Tuples (Immutability)",
        iconName: "DatabaseOutlined",
        type: "lesson",
        difficulty: "Intermediate",
        content:
          "Tuples are immutable collections.\nExample:\n`coords = (35.6895, 139.6917)`",
      },
    ],
  },
  {
    id: "m4",
    title: "Functions & Scope",
    lessons: [
      {
        id: "l13",
        title: "Introduction to Functions",
        iconName: "FunctionOutlined",
        type: "lesson",
        difficulty: "Intermediate",
        content:
          'Functions are reusable blocks of code.\n\nExample:\n`def greet(name):`\n`    return f"Hello, {name}"`',
      },
      {
        id: "l14",
        title: "Multiple Parameters",
        iconName: "FunctionOutlined",
        type: "exercise",
        difficulty: "Intermediate",
        content:
          "Write `calculate_total(subtotal, tax_rate)` and return final price.",
        starterCode:
          "# Define your function here\n\n\n# Call the function and print the result\n",
        solution:
          "def calculate_total(subtotal, tax_rate):\n    return subtotal + (subtotal * tax_rate)\n\nprint(calculate_total(100, 0.05))",
      },
      {
        id: "l15",
        title: "Understanding Scope",
        iconName: "FunctionOutlined",
        type: "lesson",
        difficulty: "Advanced",
        content:
          "Variables inside functions are local. Variables outside are global.",
      },
    ],
  },
  {
    id: "m5",
    title: "Modules & Libraries",
    lessons: [
      {
        id: "l16",
        title: "Importing Modules",
        iconName: "ApiOutlined",
        type: "lesson",
        difficulty: "Advanced",
        content:
          "Use `import` to access Python libraries.\n\nExample:\n`import datetime`",
      },
      {
        id: "l17",
        title: "The Math Module",
        iconName: "ApiOutlined",
        type: "exercise",
        difficulty: "Advanced",
        content:
          "Import `math` and use `math.sqrt()` to find the square root of 144.",
        starterCode:
          "# Import the module\n\n\n# Calculate the square root and print it\n",
        solution: "import math\nanswer = math.sqrt(144)\nprint(answer)",
      },
      {
        id: "l18",
        title: "The Random Module",
        iconName: "ApiOutlined",
        type: "exercise",
        difficulty: "Advanced",
        content: "Import `random` and simulate a 6-sided dice roll.",
        starterCode: "# Import the module\n\n\n# Roll the dice and print\n",
        solution: "import random\nroll = random.randint(1, 6)\nprint(roll)",
      },
    ],
  },
];

// --- API Simulation ---
export const fetchCourseModules = async (): Promise<Module[]> => {
  await new Promise((resolve) => setTimeout(resolve, 800));
  return courseCurriculum;
};
